import {
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

export type EditorialSelectSize = 'sm' | 'inline';

export interface EditorialSelectOption {
  value: string;
  label: string;
}

/**
 * Select editorial (pílula + chevron colado ao texto), com menu no estilo
 * iOS/glass. Substitui o `<select>` nativo para alinhar o chevron ao rótulo
 * atual — reaproveita as classes `ios-menu` do design system do front-22.
 */
@Component({
  selector: 'app-editorial-select',
  standalone: true,
  template: `
    <div [class]="block() ? 'relative block w-full md:inline-block md:w-auto' : 'relative inline-block'">
      <button
        type="button"
        aria-haspopup="listbox"
        [attr.aria-expanded]="open()"
        [attr.aria-label]="ariaLabel()"
        (click)="toggle()"
        class="inline-flex items-center gap-1.5 rounded-full border border-border bg-card shadow-sm transition hover:border-primary/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        [class]="triggerClasses()"
      >
        <span class="truncate">{{ current()?.label ?? value() }}</span>
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="h-3.5 w-3.5 shrink-0 transition-transform"
          [class.rotate-180]="open()"
          [class.text-muted-foreground]="size() === 'sm'"
          [class.text-primary]="size() === 'inline'"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      @if (open()) {
        <div
          role="listbox"
          [attr.aria-label]="ariaLabel()"
          class="ios-menu ios-menu--light absolute z-50 mt-2 max-w-[calc(100vw-1.5rem)]"
          style="--ios-tx: 0"
          [class]="menuClasses()"
        >
          @for (option of options(); track option.value) {
            <button
              type="button"
              role="option"
              [attr.aria-selected]="option.value === value()"
              (click)="select(option.value)"
              class="ios-menu-item"
              [class.ios-menu-item--active]="option.value === value()"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="h-3.5 w-3.5 shrink-0"
                [class.opacity-100]="option.value === value()"
                [class.opacity-0]="option.value !== value()"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
              <span class="flex-1 text-left">{{ option.label }}</span>
            </button>
          }
        </div>
      }
    </div>
  `,
})
export class EditorialSelectComponent {
  readonly options = input<EditorialSelectOption[]>([]);
  readonly value = input.required<string>();
  readonly ariaLabel = input<string>('');
  readonly align = input<'left' | 'right'>('right');
  readonly size = input<EditorialSelectSize>('sm');
  /** Largura total no mobile (e automática no desktop). */
  readonly block = input(false);
  readonly valueChange = output<string>();

  protected readonly open = signal(false);
  private readonly elementRef = inject(ElementRef);

  protected readonly current = computed(() =>
    this.options().find((o) => o.value === this.value()),
  );

  protected readonly triggerClasses = computed(() => {
    const base =
      this.size() === 'inline'
        ? 'py-0.5 pl-3 pr-2 text-lg md:text-xl font-display font-semibold text-primary hover:bg-primary/5'
        : 'h-8 px-3 text-xs font-medium text-foreground hover:text-primary';
    return this.block()
      ? `${base} w-full justify-between md:w-auto md:justify-start`
      : base;
  });

  protected readonly menuClasses = computed(() => {
    const width = this.block()
      ? 'w-full md:w-auto md:min-w-[180px]'
      : 'min-w-[180px]';
    const align =
      this.align() === 'right' ? 'right-0 origin-top-right' : 'left-0';
    return `${width} ${align}`;
  });

  protected toggle(): void {
    this.open.update((v) => !v);
  }

  protected select(value: string): void {
    this.valueChange.emit(value);
    this.open.set(false);
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.elementRef.nativeElement.contains(event.target)) {
      this.open.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.open.set(false);
  }
}
